import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import styles from './Maps.module.css';
import { useThemes } from '@hooks/useThemes';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { SITE_URLS } from '@constants';
import { Modules, useModule } from '@hooks/useModule';
import { Map } from '@components/atoms/Map';
import { useGetStoresLocations } from '@services/useGetStoresLocations';
import { useData } from '@hooks/useData';
import { useHtmlTransformer } from '@hooks/useHtmlTransformer';

/* #TODO: FIX STYLES */
export const Maps: FC = () => {
    const { transformHtml } = useHtmlTransformer();
    const { allSettings } = useData();

    return (
        <div className={`${styles['maps-tabs']} container-fluid px-0`} data-testid="maps">
            <div className="text-center mb-1 mb-sm-4 px-3">
                <Typography
                    classes="mb-0"
                    appearanceLevel={TypographyAppearanceVariants.level2}
                    tagVariant={TypographyTagVariants.h2}>
                    {allSettings?.data?.content_homepage_maps_title &&
                        transformHtml(allSettings?.data?.content_homepage_maps_title)}
                </Typography>
            </div>
            <SearchForm />
            <Tabs />
        </div>
    );
};

const SearchForm: FC = () => {
    const { t } = useTranslation();
    const { navigate } = useCustomNavigate();

    const translations = {
        searchPlaceholder: t('maps-component.search.placeholder'),
    };

    const [fieldSearch, setFieldSearch] = useState<string>('');

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && fieldSearch.trim()) {
            navigate(`/${SITE_URLS.merchants}?text=${encodeURIComponent(fieldSearch)}`);
        }
    };

    return (
        <div className="d-flex justify-content-center pt-2 px-3 mb-5">
            <div className={styles['search-container']}>
                <Input
                    type={InputTypes.text}
                    variant={InputVariants.searchInput}
                    value={fieldSearch}
                    onChange={setFieldSearch}
                    onKeyDown={handleKeyDown}
                    placeholder={translations.searchPlaceholder}
                />
            </div>
        </div>
    );
};

const Tabs: FC = () => {
    const { useGrayscaleTheme } = useThemes();
    const { isModuleActive, module360HasEmbed, moduleImageHasImage } = useModule();
    const { request: locations } = useGetStoresLocations();
    const { storesLocations } = useData();
    const { t } = useTranslation();

    const translations = {
        mapAlt: t('maps-component.map.alt'),
    };

    const [active, setActive] = useState<number | null>(() => {
        const firstActiveIndex = [Modules.map, Modules.map360, Modules.image].findIndex((module) => isModuleActive(module));
        return firstActiveIndex !== -1 ? firstActiveIndex : null;
    });
    const activeModules = [isModuleActive(Modules.map), isModuleActive(Modules.map360), isModuleActive(Modules.image)].filter(
        Boolean
    );

    useEffect(() => {
        locations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeModules.length > 0) {
            const firstActiveIndex = [Modules.map, Modules.map360, Modules.image].findIndex((module) => isModuleActive(module));

            setActive((prevActive) => {
                return prevActive === null && firstActiveIndex !== -1 ? firstActiveIndex : prevActive;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeModules]);

    const dataLocations = Object.entries(storesLocations?.data ?? {}).flatMap(([city, locations]) => {
        if (!Array.isArray(locations)) {
            console.warn(`Invalid data for city ${city}:`, locations);
            return [];
        }

        return locations.map((location) => ({
            uuid: location.uuid ?? '',
            name: location.name ?? city,
            latitude: location.latitude ?? 0,
            longitude: location.longitude ?? 0,
        }));
    });

    const embedUrl = module360HasEmbed();
    const moduleImage = moduleImageHasImage();

    if (activeModules.length >= 2) {
        return (
            <div>
                <ul className={`${useGrayscaleTheme ? styles['theme-grayscale'] : ''} nav justify-content-center`} role="tablist">
                    {isModuleActive(Modules.map) && (
                        <li className={`nav-item ${styles['nav-item']}`} role="presentation">
                            <button
                                type="button"
                                role="tab"
                                className={`nav-link ${styles['nav-link']} ${active === 0 ? styles.active : ''}`}
                                aria-selected={active === 0 ? 'true' : 'false'}
                                onClick={() => setActive(0)}>
                                <svg width="24" height="29" viewBox="0 0 24 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M12 0C5.38357 0 0.000122166 5.20405 0.000122166 11.5928C-0.0433774 20.938 11.544 28.6868 12 29C12 29 24.0434 20.938 23.9999 11.6C23.9999 5.20405 18.6164 0 12 0ZM12 17.4C8.68504 17.4 6.00006 14.8045 6.00006 11.6C6.00006 8.3955 8.68504 5.8 12 5.8C15.315 5.8 17.9999 8.3955 17.9999 11.6C17.9999 14.8045 15.315 17.4 12 17.4Z"
                                        className={`color-change ${styles['color-change']}`}
                                        fill="#A9A9A9"
                                    />
                                    <circle cx="12" cy="12" r="8" fill="white" />
                                </svg>
                            </button>
                        </li>
                    )}
                    {isModuleActive(Modules.map360) && (
                        <li className={`nav-item ${styles['nav-item']}`} role="presentation">
                            <button
                                type="button"
                                role="tab"
                                className={`nav-link ${styles['nav-link']} ${active === 1 ? styles.active : ''}`}
                                aria-selected={active === 1 ? 'true' : 'false'}
                                onClick={() => setActive(1)}>
                                <i className="fa-regular fa-360-degrees" />
                            </button>
                        </li>
                    )}
                    {isModuleActive(Modules.image) && (
                        <li className={`nav-item ${styles['nav-item']}`} role="presentation">
                            <button
                                type="button"
                                role="tab"
                                className={`nav-link ${styles['nav-link']} ${active === 2 ? styles.active : ''}`}
                                aria-selected={active === 2 ? 'true' : 'false'}
                                onClick={() => setActive(2)}>
                                <i className="fa-regular fa-store" />
                            </button>
                        </li>
                    )}
                </ul>
                <div className="tab-content">
                    {isModuleActive(Modules.map) && (
                        <div className={`tab-pane fade ${active === 0 ? 'show active' : ''}`} role="tabpanel">
                            <Map locations={dataLocations} style={'{"height": "700px", "objectFit": "cover"}'} />
                        </div>
                    )}
                    {isModuleActive(Modules.map360) && embedUrl && (
                        <div className={`tab-pane fade ${active === 1 ? 'show active' : ''}`} role="tabpanel">
                            <iframe
                                src={embedUrl}
                                className="w-100"
                                style={{ height: '700px', objectFit: 'cover', border: 'none' }}
                                allowFullScreen
                                loading="lazy"
                                title="Map"></iframe>
                        </div>
                    )}
                    {isModuleActive(Modules.image) && moduleImage && (
                        <div className={`tab-pane fade ${active === 2 ? 'show active' : ''}`} role="tabpanel">
                            <img
                                src={moduleImage?.src}
                                className="w-100"
                                style={{ height: '700px', objectFit: 'cover' }}
                                alt={translations.mapAlt}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    } else if (activeModules.length === 1) {
        if (isModuleActive(Modules.map)) {
            return (
                <div>
                    <Map locations={dataLocations} style={'{"height": "700px", "objectFit": "cover"}'} />
                </div>
            );
        }
        if (isModuleActive(Modules.map360) && embedUrl) {
            return (
                <div>
                    <iframe
                        src={embedUrl}
                        className="w-100"
                        style={{ height: '700px', objectFit: 'cover', border: 'none' }}
                        allowFullScreen
                        loading="lazy"
                        title="Map"></iframe>
                </div>
            );
        }
        if (isModuleActive(Modules.image) && moduleImage) {
            return (
                <div>
                    <img
                        src={moduleImage?.src}
                        className="w-100"
                        style={{ height: '700px', objectFit: 'cover' }}
                        alt={translations.mapAlt}
                    />
                </div>
            );
        }
    }
    return null;
};
